import React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ErrorBoundary } from "./components/ErrorBoundary";

import { JobNotificationCenter } from "./components/JobNotificationCenter";
import ProfileCompletionGate from "./components/ProfileCompletionGate";
import { storage } from "./lib/storage";
import BillingCancelPage from "./pages/BillingCancelPage";
import BillingSuccessPage from "./pages/BillingSuccessPage";
import CharacterSwitch from "./pages/CharacterSwitch";
import Chat from "./pages/Chat";
import CommercialTransactionsPage from "./pages/CommercialTransactionsPage";
import GoogleLinkPopup from "./pages/GoogleLinkPopup";
import GoogleOAuthPopupComplete from "./pages/GoogleOAuthPopupComplete";
import GroupTalkHub from "./pages/GroupTalkHub";
import GroupTalkPreview from "./pages/GroupTalkPreview";
import GroupTalkSessionList from "./pages/GroupTalkSessionList";
import GroupTalkSession from "./pages/GroupTalkSession";
import Home from "./pages/Home";
import LineLinkPopup from "./pages/LineLinkPopup";
import LineEntryPage from "./pages/LineEntryPage";
import LineLoginPage from "./pages/LineLoginPage";
import LineOAuthPopupComplete from "./pages/LineOAuthPopupComplete";
import LineRegisterStart from "./pages/LineRegisterStart";
import LineRegisterCallback from "./pages/LineRegisterCallback";
import LineRegisterComplete from "./pages/LineRegisterComplete";
import NewsGoogleReconsentComplete from "./pages/NewsGoogleReconsentComplete";
import EmailInviteOnboarding from "./pages/EmailInviteOnboarding";
import EmailOtpEmailPage from "./pages/EmailOtpEmailPage";
import EmailOtpVerifyPage from "./pages/EmailOtpVerifyPage";
import ExecutiveMemoDetailPage from "./pages/ExecutiveMemoDetailPage";
import ExecutiveMemosPage from "./pages/ExecutiveMemosPage";
import LandingPage from "./pages/LandingPage";
import PlansPage from "./pages/PlansPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import ProfileLoginMethodsPage from "./pages/ProfileLoginMethodsPage";
import ProfileSettings from "./pages/ProfileSettings";
import ReceiptDetailPage from "./pages/ReceiptDetailPage";
import ReceiptHistoryPage from "./pages/ReceiptHistoryPage";
import ReceiptPopup from "./pages/ReceiptPopup";
import RequestCompanyResearchJobDetailPage from "./pages/RequestCompanyResearchJobDetailPage";
import RequestCompanyResearchJobListPage from "./pages/RequestCompanyResearchJobListPage";
import RequestCompanyResearchPage from "./pages/RequestCompanyResearchPage";
import RequestEntry from "./pages/RequestEntry";
import RequestExecutiveMemosPage from "./pages/RequestExecutiveMemosPage";
import RequestNewsPage from "./pages/RequestNewsPage";
import RequestReportPage from "./pages/RequestReportPage";
import RequestSchedulesPage from "./pages/RequestSchedulesPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import Splash from "./pages/Splash";
import StartPage from "./pages/StartPage";
import TalkEntry from "./pages/TalkEntry";
import TermsPage from "./pages/TermsPage";

const IS_STAGING = process.env.REACT_APP_ENV === "staging";
const PUBLIC_LANDING_PAGE_PATH = "/landing";

function requireProfile(element: React.ReactElement) {
  return <ProfileCompletionGate>{element}</ProfileCompletionGate>;
}

// 未ログインのトップページは公開LPを表示し、ログイン済みは既存の起動フロー（/splash → /home）へ送る。
function PublicRootEntry() {
  const { accessToken, refreshToken } = storage.getAuth();
  if (accessToken || refreshToken) {
    return <Navigate to="/splash" replace />;
  }
  return <LandingPage />;
}

export default function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      {IS_STAGING && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: "24px",
            background: "#f97316",
            color: "#fff",
            fontSize: "11px",
            fontWeight: "bold",
            letterSpacing: "0.1em",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          STAGING
        </div>
      )}
      <JobNotificationCenter />
      <Routes>
        <Route path="/" element={<PublicRootEntry />} />
        <Route path={PUBLIC_LANDING_PAGE_PATH} element={<LandingPage />} />
        <Route path="/start" element={<StartPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/splash" element={<Splash />} />
        <Route path="/onboarding" element={<EmailInviteOnboarding />} />
        <Route path="/email-otp/email" element={<EmailOtpEmailPage />} />
        <Route path="/email-otp/verify" element={<EmailOtpVerifyPage />} />
        <Route path="/home" element={requireProfile(<Home />)} />
        <Route path="/profile" element={<ProfileSettings />} />
        <Route path="/profile/login-methods" element={<ProfileLoginMethodsPage />} />
        <Route path="/character-switch" element={requireProfile(<CharacterSwitch />)} />
        <Route path="/talk" element={requireProfile(<TalkEntry />)} />
        <Route path="/talk/group" element={requireProfile(<GroupTalkHub />)} />
        <Route path="/talk/group/preview" element={requireProfile(<GroupTalkPreview />)} />
        <Route path="/talk/group/sessions" element={requireProfile(<GroupTalkSessionList />)} />
        <Route path="/talk/group/sessions/:sessionId" element={requireProfile(<GroupTalkSession />)} />
        <Route path="/request" element={requireProfile(<RequestEntry />)} />
        <Route path="/request/company-research" element={requireProfile(<RequestCompanyResearchPage />)} />
        <Route path="/request/company-research/jobs" element={requireProfile(<RequestCompanyResearchJobListPage />)} />
        <Route path="/request/company-research/jobs/:jobId" element={requireProfile(<RequestCompanyResearchJobDetailPage />)} />
        <Route path="/request/executive-memos" element={requireProfile(<RequestExecutiveMemosPage />)} />
        <Route path="/request/handwritten-notes" element={<Navigate to="/request" replace />} />
        <Route path="/request/news" element={requireProfile(<RequestNewsPage />)} />
        <Route path="/request/report" element={requireProfile(<RequestReportPage />)} />
        <Route path="/request/schedules" element={requireProfile(<RequestSchedulesPage />)} />
        <Route path="/executive-memos" element={requireProfile(<ExecutiveMemosPage />)} />
        <Route path="/executive-memos/:memoId" element={requireProfile(<ExecutiveMemoDetailPage />)} />
        {/* AI社長室の通常利用前に必須プロファイル登録を求めるため、OCR系のreceipt導線もgate対象にする。 */}
        <Route path="/receipt" element={requireProfile(<ReceiptPopup />)} />
        <Route path="/receipt/history" element={requireProfile(<ReceiptHistoryPage />)} />
        <Route path="/receipt/:jobId" element={requireProfile(<ReceiptDetailPage />)} />
        <Route path="/google-link" element={<GoogleLinkPopup />} />
        <Route path="/google-link/complete" element={<GoogleOAuthPopupComplete />} />
        <Route path="/news/google-consent/complete" element={<NewsGoogleReconsentComplete />} />
        <Route path="/line-link" element={<LineLinkPopup />} />
        <Route path="/line-link/complete" element={<LineOAuthPopupComplete />} />
        <Route path="/line" element={<LineEntryPage />} />
        <Route path="/line-login" element={<LineLoginPage />} />
        <Route path="/line-register" element={<LineRegisterStart />} />
        <Route path="/line-register/callback" element={<LineRegisterCallback />} />
        <Route path="/line-register/complete" element={<LineRegisterComplete />} />
        <Route path="/chat" element={requireProfile(<Chat />)} />
        <Route path="/billing/success" element={<BillingSuccessPage />} />
        <Route path="/billing/cancel" element={<BillingCancelPage />} />
        <Route path="/plans" element={<PlansPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/commercial-transactions" element={<CommercialTransactionsPage />} />
        <Route path="*" element={<Navigate to="/splash" replace />} />
      </Routes>
    </BrowserRouter>
    </ErrorBoundary>
  );
}
